import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useNavigate, useParams } from "react-router-dom";

import "./GameFlowPage.scss";
import StartGamePage from "../StartGamePage/StartGamePage";
import GameResultsPage from "../GameResultsPage/GameResultsPage";
import {
  didUserClickToInitiateGameAtom,
  gameFlowStateAtomWithStorage,
  gameTypeAtom,
} from "../../jotai/atoms/games";
import {
  GameFlowState,
  GameType,
  ROUTES,
  TOURNAMENT_TO_GAME_TYPE_MAP,
} from "../../utils/constants";
import useGetMyTournamentEntryListQuery from "../../react-query/queries/use-get-my-tournament-entry-list";
import LoadingOverlay, {
  OverlayVariant,
} from "../../components/LoadingOverlay/LoadingOverlay";
import useAuth from "../../hooks/useAuth";
import { showErrorToast } from "../../utils/show-toast";
import useGetTournamentDetailsQuery from "../../react-query/queries/use-get-tournament-details";
import { renderHeaders } from "../../utils/renderHeaders";
import useGameDetailsBySlugQuery from "../../react-query/queries/use-game-details-by-slug";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../utils/helpers";

interface GameFlowPageWithHeadersProps {
  contestType: string;
  activeGameName?: string;
  posterImage?: string;
}

function GameFlowPageWithHeaders({
  children,
  contestType,
  activeGameName,
  posterImage,
}: PropsWithChildren<GameFlowPageWithHeadersProps>) {
  const { t } = useTranslation("metadata");

  return (
    <>
      {renderHeaders({
        title: t("sigma_see_results_for_game_title", {
          activeGameName,
          contestType: capitalizeFirstLetter(contestType),
        }),
        description: t("sigma_results_description"),
        ogTitle: t("sigma_see_results_for_game_og_title", {
          activeGameName,
          contestType: capitalizeFirstLetter(contestType),
        }),
        image: posterImage,
      })}
      {children}
    </>
  );
}

function GameFlowPage() {
  const navigate = useNavigate();
  const { tournamentId, gameSlug } = useParams();
  const { isLoggedIn } = useAuth();
  const [gameFlowState, setGameFlowState] = useAtom(
    gameFlowStateAtomWithStorage
  );

  const gameType = useAtomValue(gameTypeAtom);
  const didUserClickToInitiateGame = useAtomValue(
    didUserClickToInitiateGameAtom
  );
  const isPracticeGame =
    gameType === GameType.PRACTICE &&
    !!tournamentId?.includes(GameType.PRACTICE);
  const isSoloGame = gameType === GameType.SOLO;
  const isSoloOrPracticeGame = isPracticeGame || isSoloGame;

  const { data: gameDetails } = useGameDetailsBySlugQuery({
    slug: gameSlug ?? "",
    enabled: isSoloOrPracticeGame,
  });
  const { data: tournamentDetails } = useGetTournamentDetailsQuery({
    tournamentId: tournamentId || "",
    enabled: !isSoloOrPracticeGame,
  });
  const { name: tournamentGameName, poster: tournamentGamePoster } =
    tournamentDetails?.game ?? {};
  const {
    data: entryList,
    isLoading: isEntryListLoading,
    error: tournamentParticipantsError,
  } = useGetMyTournamentEntryListQuery({
    tournamentId,
    enabled:
      !!tournamentId && !didUserClickToInitiateGame && !isSoloOrPracticeGame,
    retry: 0,
  });

  const hasScoreForTournament = useMemo(
    () => !!entryList?.records.some((entry) => typeof entry.score === "number"),
    [entryList?.records]
  );
  const isParticipant = !!entryList?.records && entryList.records.length > 0;
  const shouldShowResult = isParticipant && hasScoreForTournament;
  const contestType = useMemo(
    () =>
      tournamentDetails
        ? TOURNAMENT_TO_GAME_TYPE_MAP[tournamentDetails.type]
        : GameType.PRACTICE,
    [tournamentDetails]
  );

  useEffect(() => {
    if (isEntryListLoading || isSoloOrPracticeGame) return;
    if (shouldShowResult) {
      setGameFlowState(GameFlowState.VIEWING_RESULTS);
      return;
    }
    if (!gameSlug && gameType === GameType.BATTLE) {
      navigate(`${ROUTES.GAMES.ROOT}`);
      return;
    }
    if (gameFlowState === null && !hasScoreForTournament) {
      setGameFlowState(GameFlowState.STARTING_GAME);
    }
  }, [
    gameFlowState,
    gameSlug,
    gameType,
    hasScoreForTournament,
    isEntryListLoading,
    isSoloOrPracticeGame,
    navigate,
    setGameFlowState,
    shouldShowResult,
  ]);

  useEffect(() => {
    if (isSoloOrPracticeGame || isLoggedIn) return;
    navigate(`${ROUTES.GAMES.ROOT}`);
  }, [isLoggedIn, isSoloOrPracticeGame, navigate]);

  useEffect(() => {
    if (
      !tournamentId ||
      didUserClickToInitiateGame ||
      isSoloOrPracticeGame ||
      isEntryListLoading
    ) {
      return;
    }

    if (!isParticipant) {
      showErrorToast("Tournament not found");
      navigate(`${ROUTES.GAMES.ROOT}`);
      return;
    }
  }, [
    didUserClickToInitiateGame,
    isSoloOrPracticeGame,
    navigate,
    tournamentId,
    tournamentParticipantsError,
    isParticipant,
    isEntryListLoading,
  ]);

  if (isEntryListLoading)
    return (
      <GameFlowPageWithHeaders
        activeGameName={tournamentGameName ?? gameDetails?.name}
        contestType={contestType}
        posterImage={tournamentGamePoster ?? gameDetails?.poster}
      >
        <div className="game-flow-page">
          <LoadingOverlay variant={OverlayVariant.BLOCKS} />
        </div>
      </GameFlowPageWithHeaders>
    );

  if (gameFlowState === GameFlowState.STARTING_GAME)
    return (
      <GameFlowPageWithHeaders
        activeGameName={tournamentGameName ?? gameDetails?.name}
        contestType={contestType}
        posterImage={tournamentGamePoster ?? gameDetails?.poster}
      >
        <StartGamePage
          tournamentDetails={tournamentDetails}
          gameName={tournamentGameName ?? gameDetails?.name}
        />
      </GameFlowPageWithHeaders>
    );

  if (
    gameFlowState === GameFlowState.VIEWING_RESULTS ||
    gameFlowState === GameFlowState.VIEWING_RESULTS_FOR_FAILED_GAME
  )
    return (
      <GameFlowPageWithHeaders
        activeGameName={tournamentGameName ?? gameDetails?.name}
        contestType={contestType}
        posterImage={tournamentGamePoster ?? gameDetails?.poster}
      >
        <GameResultsPage tournamentDetails={tournamentDetails} />
      </GameFlowPageWithHeaders>
    );

  return null;
}

export default GameFlowPage;
